/**
 * **常量节点** —— 每种基础类型一个**独立**节点。
 *
 * ## 为什么不做成「一个常量节点 + 类型下拉」
 *
 * 用户明确要求：「不要用下拉切换类型，只能直接创建对应类型节点」。
 *
 * 这不是口味问题。下拉切换类型会带来一串麻烦：
 *   · **端口类型会变**。切类型等于把 `out` 端口的类型换掉，
 *     已经连好的线要么静默失效、要么被迫断开 —— 两种都不好；
 *   · **字面量的表示会变**。`text` 的编辑器和 `color` 的取色器不是同一个控件，
 *     切换时要重建整个行结构，节点上已有的其它状态（宽度、位置、选中）也容易丢；
 *   · **搜索/添加变模糊**。「常量」在菜单里只有一项，人得先加再切，
 *     而 Shift+A 里打 `color` 搜不到它 —— 而人找的正是「一个颜色」。
 *
 * 一种类型一个节点，就都是直的了：端口类型固定、编辑器固定、
 * 搜索词精确。代价是菜单项变多，而那个代价是可见、可接受的。
 *
 * ## 覆盖哪些类型
 *
 * 按 `types/socket.ts` 的类型树：
 *
 * | 类型 | 节点 | 字面量怎么编辑 |
 * |---|---|---|
 * | `text` | `const.text` | 文本控件 |
 * | `number` | `const.number` | 数值控件 |
 * | `boolean` | `const.boolean` | 开关 |
 * | `color` | `const.color` | 取色器 |
 * | `datetime` | `const.datetime` | **ISO 文本**，求值时解析成 epoch 毫秒 |
 * | `list` | `const.list` | 变参行：把若干输入**收成**一个列表 |
 *
 * ⚠️ `datetime` 用的是文本控件而不是日期控件：控件集合里**没有**日期控件
 * （`WidgetKind` 只有 text/number/boolean/enum/color/custom）。
 * 与其造一个新控件，不如让字面量就是 ISO 字符串、求值时解析 ——
 * 解析不出来时输出 `null` 而不是 `NaN`（见 `evaluators.ts`）。
 *
 * ⚠️ `list` 是**结构类型**，做不成纯字面量：当前类型系统里 `list` 不参数化
 * （没有 `list<number>` 这种），一个「列表字面量」的每个元素该是什么类型没有答案。
 * 所以它做成了**收集**节点：变参行每一路是 `any` 输入，连什么收什么。
 * 这样「三个数值常量 → 列表 → 循环」就能跑通。
 *
 * ⚠️ **没有** `rich` / `page` / `component` 的常量：
 *   · `rich` 需要富文本段编辑器（控件集合里没有）；
 *   · `page` / `component` / `layout` 是**引用**文档对象的类型，
 *     它们的「常量」是「指向哪个页面/实例」，那是选择器而不是字面量。
 * 这三类要么等各自的编辑器，要么本来就该由数据源包提供。
 */
import type { NodeTypeDef } from './package'
import { NODE_ICONS } from './nodeIcons'
import type { NodeRow } from '../types/node'

/** 常量节点的公共收尾：一个输出行 */
const outRow = (socketType: string): NodeRow => ({
  kind: 'output',
  id: 'out',
  label: '值',
  socket: { id: 'out', type: socketType as never },
})

/**
 * 常量节点的公共前缀。
 *
 * ⚠️ `editorOnly` **不是**：它们要参与求值（求值器就是「把字面量送出去」）。
 */
function constNode(options: {
  type: string
  title: string
  width: number
  hint: string
  keywords: string[]
  /** 特化图标（24×24 stroke 路径）—— 见 `nodeIcons.ts` 的说明 */
  icon?: string
  /**
   * 字面量行模板。
   *
   * ⚠️ 这里**不再需要工厂函数**了 —— 行是数据，`makeModelOfType` 会为
   * 每个实例深拷贝（`cloneRows`）。以前必须写成 `() => NodeRow` 才行，
   * 忘了就是「改一个数值常量改掉画布上所有同类型常量」。
   * 现在这个坑由机制堵住，而不是靠写的人记得。
   */
  literal?: NodeRow
  socketType: string
}): NodeTypeDef {
  return {
    type: options.type,
    title: options.title,
    category: 'value',
    width: options.width,
    hint: options.hint,
    keywords: options.keywords,
    ...(options.icon ? { icon: options.icon } : {}),
    /* 行是**数据**：模板在这里，实例的拷贝由 `makeModelOfType` 负责 */
    rows: options.literal
      ? [options.literal, outRow(options.socketType)]
      : [outRow(options.socketType)],
  }
}

export const CONST_NODE_TYPES: NodeTypeDef[] = [
  constNode({
    type: 'const.text',
    icon: NODE_ICONS['const.text'],
    title: '常量 · 文本',
    width: 180,
    hint: '一个固定的文本值',
    keywords: ['const', 'string', 'text', '常量', '文本', '字符串'],
    socketType: 'text',
    literal: {
      /*
       * ⚠️ `property` 而**不是** `input`：常量没有输入。
       *
       * 一开始写成 `input`（带 `value` 端口），结果是画布上摆出一个可以接线的
       * 输入点 —— 而求值器读的是字面量、**根本不看那条线**。
       * 于是「接上去什么也没发生」，用户只会以为坏了。
       * 没有输入就不该画出输入点。
       */
      kind: 'property',
      id: 'value',
      label: '值',
      /*
       * ★ **多行文本域**（2027，用户："常量文本的值需要支持文本框"）。
       *
       * 一个常量文本要能装下一段地址 / 一句文案 / 一小段 JSON ——
       * 单行输入框里那些内容只能横向滚，看不见全貌也没法换行。
       * 控件本来就是现成的（`TextWidget.multiline`，打印包的参数早就在用），
       * 这里只是把这一行的控件换成它。
       *
       * ⚠️ 3 行起步、高度可拖（控件自己把拖出来的高度回写进 `height`，
       * 跟着模板保存）—— 常量节点的高度本来就随内容自适应。
       */
      widget: { id: 'value', kind: 'text', value: '', multiline: true, rows: 3 },
    },
  }),

  constNode({
    type: 'const.number',
    icon: NODE_ICONS['const.number'],
    title: '常量 · 数值',
    width: 172,
    hint: '一个固定的数值',
    keywords: ['const', 'number', 'num', '常量', '数值', '数字'],
    socketType: 'number',
    literal: {
      /* 同 `const.text`：常量没有输入端口 */
      kind: 'property',
      id: 'value',
      label: '值',
      /*
       * ⚠️ 不设 min/max：常量就是「随便什么数」。
       * 限成 0–100 是**业务**的约束（比如百分比），不该由常量节点替人决定 ——
       * 那种约束是数据源 / 函数包该表达的事。
       */
      widget: { id: 'value', kind: 'number', value: 0, precision: 3 },
    },
  }),

  constNode({
    type: 'const.boolean',
    icon: NODE_ICONS['const.boolean'],
    title: '常量 · 布尔',
    width: 168,
    hint: '一个固定的真 / 假值',
    keywords: ['const', 'bool', 'boolean', 'true', 'false', '常量', '布尔', '真假'],
    socketType: 'boolean',
    literal: {
      kind: 'property',
      id: 'value',
      label: '值',
      widget: { id: 'value', kind: 'boolean', value: false },
    },
  }),

  constNode({
    type: 'const.color',
    icon: NODE_ICONS['const.color'],
    title: '常量 · 颜色',
    width: 180,
    hint: '一个固定的颜色',
    keywords: ['const', 'color', 'colour', '常量', '颜色', '色彩'],
    socketType: 'color',
    literal: {
      kind: 'property',
      id: 'value',
      label: '值',
      widget: { id: 'value', kind: 'color', value: '#1a1a1a' },
    },
  }),

  constNode({
    type: 'const.datetime',
    icon: NODE_ICONS['const.datetime'],
    title: '常量 · 时间',
    width: 196,
    hint: '时间文本（ISO），求值时解析成 epoch 毫秒；纯日期按 UTC，月/日补不补零都一样',
    keywords: ['const', 'datetime', 'date', 'time', '常量', '时间', '日期'],
    socketType: 'datetime',
    literal: {
      kind: 'property',
      id: 'value',
      label: '值',
      /*
       * ⚠️ 默认空串而不是 `new Date()`：默认值不该是「你打开模板的那一刻」
       * —— 一份模板今天存、明天打开就变成另一个时间，那不是"常量"。
       *
       * ★ **空着的时候输出"现在"**（2027，用户："常量时间这个节点，空着的时候，
       * 能不能输出当前时间"）。所以空串的含义是"不钉死"，由求值器回答当时
       * 是几点（见 `evaluators.ts` 的 `const.datetime`）—— 于是这个节点的
       * 默认形态就是「打印当前时间」，而填上值就变回一个真正的常量。
       */
      widget: { id: 'value', kind: 'text', value: '', placeholder: '留空 = 当前时间' },
    },
  }),

  constNode({
    type: 'const.list',
    icon: NODE_ICONS['const.list'],
    title: '常量 · 列表',
    width: 200,
    hint: '把若干输入收成一个列表',
    keywords: ['const', 'list', 'array', '常量', '列表', '数组'],
    socketType: 'list',
    /*
     * ⚠️ 变参行 + `any` 输入：列表的元素类型由**连进来的东西**决定，
     * 节点自己不预设。接线规则仍然按 `any` 放行，所以这是合法的。
     */
    literal: {
      kind: 'repeater',
      id: 'items',
      label: '元素',
      socketType: 'any',
      min: 1,
      max: 32,
    },
  }),
]
